import PasswordInput from '@components/Inputs/PasswordInput';
import InputField from '@components/InputField';
import {
    Avatar,
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    MenuItem,
    Stack,
    Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { Controller, FormProvider, UseFormReturn } from 'react-hook-form';
import { UpercutFormValues, UpercutUpdateFormValues } from './types';
import { generatePassword } from '@helper/string.utils';

interface RoleOption {
    _id: string;
    name: string;
}

interface UpercutFormProps {
    formMode: 'create' | 'edit';
    formMethods: UseFormReturn<UpercutFormValues | UpercutUpdateFormValues>;
    onSubmit: (values: UpercutFormValues | UpercutUpdateFormValues) => Promise<void>;
    onCancel: () => void;
    isSubmitting: boolean;
    roles?: RoleOption[];
}


interface RequirementItemProps {
    met: boolean;
    text: string;
}

const RequirementItem: React.FC<RequirementItemProps> = ({ met, text }) => (
    <Stack direction="row" gap="8px" alignItems="center" sx={{ height: 24 }}>
        <i
            className="fi fi-br-check"
            style={{
                fontSize: 14,
                color: met ? '#5447de' : '#939393',
                display: 'flex',
                alignItems: 'center',
            }}
        />
        <Typography
            sx={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontWeight: 500,
                fontSize: 14,
                color: '#cbcbcb',
                textDecoration: met ? 'line-through' : 'none',
                whiteSpace: 'nowrap',
            }}
        >
            {text}
        </Typography>
    </Stack>
);

const UpercutForm: React.FC<UpercutFormProps> = ({
    formMode,
    formMethods,
    onSubmit,
    onCancel,
    isSubmitting,
    roles = [],
}) => {
    const [generateRandom, setGenerateRandom] = useState(false);

    const watchPassword = formMethods.watch('password') as string | undefined;
    const watchFirstName = formMethods.watch('firstName') as string | undefined;
    const watchLastName = formMethods.watch('lastName') as string | undefined;

    const initials = `${watchFirstName?.[0] ?? ''}${watchLastName?.[0] ?? ''}`.toUpperCase();

    const pwd = watchPassword ?? '';
    const requirements = {
        length: pwd.length >= 8,
        cases: /[A-Z]/.test(pwd) && /[a-z]/.test(pwd),
        digit: /[0-9]/.test(pwd),
        special: /[.,;:!?+\-_@#$&]/.test(pwd),
    };

    const handleGenerateToggle = (checked: boolean) => {
        setGenerateRandom(checked);
        if (checked) {
            const generated = generatePassword();
            formMethods.setValue('password', generated, { shouldValidate: true });
            setTimeout(() => {
            formMethods.setValue('passwordConfirm', generated, { shouldValidate: true });
            }, 100);
        } else {
            formMethods.setValue('password', '', { shouldValidate: true });
            formMethods.setValue('passwordConfirm', '', { shouldValidate: true });
        }
    };

    return (
        <FormProvider {...formMethods}>
            <form onSubmit={formMethods.handleSubmit(onSubmit)}>
                <Box
                    sx={{
                        bgcolor: '#222426',
                        borderRadius: '16px',
                        p: '24px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '24px',
                        boxShadow: '0px 4px 80px 0px rgba(112, 144, 176, 0.14)',
                    }}
                >
                    {/* Header */}
                    <Stack spacing="8px">
                        <Typography
                            sx={{
                                fontFamily: "'LEMON MILK Pro', 'Plus Jakarta Sans', sans-serif",
                                fontWeight: 500,
                                fontSize: 25,
                                lineHeight: '40px',
                                color: '#fff',
                            }}
                        >
                            {formMode === 'create' ? 'Créer un nouvel utilisateur' : 'Modifier l\'utilisateur'}
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: "'Plus Jakarta Sans', sans-serif",
                                fontWeight: 500,
                                fontSize: 16,
                                lineHeight: '24px',
                                color: '#cbcbcb',
                            }}
                        >
                            {formMode === 'create'
                                ? 'Veuillez remplir ce formulaire pour créer un profil utilisateur en précisant le rôle souhaité et les permissions associées'
                                : 'Mettez à jour les informations et les permissions du profil utilisateur ci-dessous'}
                        </Typography>
                    </Stack>

                    {/* Profile photo section */}
                    {/* <Box
                        sx={{
                            border: '1px solid #fff',
                            borderRadius: '16px',
                            p: '24px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '16px',
                        }}
                    >
                        <Stack direction="row" gap="24px" alignItems="center">
                            <Avatar
                                sx={{
                                    width: 80,
                                    height: 80,
                                    border: '1px solid #fff',
                                    bgcolor: '#5447de',
                                    fontSize: 28,
                                    fontWeight: 600,
                                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                                    flexShrink: 0,
                                }}
                            >
                                {initials || (
                                    <i
                                        className="fi fi-br-user"
                                        style={{ fontSize: 28, display: 'flex', alignItems: 'center' }}
                                    />
                                )}
                            </Avatar>
                            <Stack spacing="8px">
                                <Typography
                                    sx={{
                                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                                        fontWeight: 600,
                                        fontSize: 20,
                                        lineHeight: '24px',
                                        color: '#fff',
                                    }}
                                >
                                    Photo de profil
                                </Typography>
                                <Typography
                                    sx={{
                                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                                        fontWeight: 500,
                                        fontSize: 13,
                                        lineHeight: '16px',
                                        color: '#c0c0c0',
                                        maxWidth: 320,
                                    }}
                                >
                                    Recommandé : Carré JPG, PNG, or GIF, mini 98 x 98 pixels, max 4 MB.
                                </Typography>
                            </Stack>
                        </Stack>
                        <Button
                            variant="outlined"
                            type="button"
                            sx={{
                                borderColor: '#fff',
                                borderRadius: '48px',
                                color: '#fff',
                                fontFamily: "'Plus Jakarta Sans', sans-serif",
                                fontWeight: 700,
                                fontSize: 20,
                                lineHeight: '32px',
                                px: '16px',
                                height: '48px',
                                textTransform: 'none',
                                flexShrink: 0,
                                '&:hover': {
                                    borderColor: '#fff',
                                    bgcolor: 'rgba(255,255,255,0.08)',
                                },
                            }}
                        >
                            Changer de photo
                        </Button>
                    </Box> */}

                    {/* Form fields */}
                    <Stack spacing="24px">
                        {/* Row 1: First Name + Last Name */}
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing="24px">
                            <Controller
                                name="firstName"
                                control={formMethods.control}
                                rules={{ required: 'Le prénom est requis' }}
                                render={({ field, fieldState }) => (
                                    <InputField
                                        {...field}
                                        label={'First Name'}
                                        placeholder="Saisissez le prénom"
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                        fullWidth
                                    />
                                )}
                            />
                            <Controller
                                name="lastName"
                                control={formMethods.control}
                                rules={{ required: 'Le nom est requis' }}
                                render={({ field, fieldState }) => (
                                    <InputField
                                        {...field}
                                        label={'Last Name'}
                                        placeholder="Saisissez le nom"
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                        fullWidth
                                    />
                                )}
                            />
                        </Stack>

                        {/* Row 2: Email */}
                        <Controller
                            name="email"
                            control={formMethods.control}
                            rules={{
                                required: "L'adresse email est requise",
                                pattern: {
                                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                    message: "Format d'email invalide",
                                },
                            }}
                            render={({ field, fieldState }) => (
                                <InputField
                                    {...field}
                                    label={'Email de connexion'}
                                    placeholder="exemple@email.com"
                                    type="email"
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message}
                                    fullWidth
                                />
                            )}
                        />

                        {/* Row 3: Role */}
                        <Controller
                            name="roleId"
                            control={formMethods.control}
                            rules={{ required: 'Le rôle est requis' }}
                            render={({ field, fieldState }) => (
                                <InputField
                                    {...field}
                                    select
                                    label={'Rôle'}
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message}
                                    fullWidth
                                    SelectProps={{ displayEmpty: true }}
                                >
                                    <MenuItem value="">
                                        <em style={{ color: '#939393' }}>
                                            Sélectionnez un rôle
                                        </em>
                                    </MenuItem>
                                    {roles.map((role) => (
                                        <MenuItem key={role._id} value={role._id}>
                                            {role.name}
                                        </MenuItem>
                                    ))}
                                </InputField>
                            )}
                        />

                        {/* Generate random password checkbox */}
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={generateRandom}
                                    onChange={(e) => handleGenerateToggle(e.target.checked)}
                                    sx={{
                                        color: '#4239b0',
                                        '&.Mui-checked': { color: '#4239b0' },
                                        p: '0 6px 0 0',
                                    }}
                                />
                            }
                            label={
                                <Typography
                                    sx={{
                                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                                        fontWeight: 500,
                                        fontSize: 14,
                                        lineHeight: '20px',
                                        color: '#fff',
                                    }}
                                >
                                    Générer un mot de passe aléatoire (envoyé par email)
                                </Typography>
                            }
                            sx={{ m: 0 }}
                        />

                        {/* Edit mode hint */}
                        {formMode === 'edit' && (
                            <Typography
                                sx={{
                                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                                    fontSize: 14,
                                    color: '#cbcbcb',
                                    lineHeight: '20px',
                                }}
                            >
                                Laissez les champs de mot de passe vides pour conserver le mot de passe actuel.
                            </Typography>
                        )}

                        {/* Password fields */}
                        <Box sx={{ width: { xs: '100%', sm: '50%' } }}>
                            <input type="password" style={{ width: 0, height: 0, opacity: 0, position: 'absolute' }} autoComplete="new-password" />
                            <Controller
                                name="password"
                                control={formMethods.control}
                                rules={{
                                    required: formMode === 'create' ? 'Le mot de passe est requis' : false,
                                    minLength: {
                                        value: 8,
                                        message: 'Le mot de passe doit contenir au moins 8 caractères',
                                    },
                                }}
                                render={({ field, fieldState }) => (
                                    <PasswordInput
                                        {...field}
                                        label={'Nouveau mot de passe'}
                                        placeholder="Saisissez le mot de passe"
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                        autoComplete="off"
                                        fullWidth
                                    />
                                )}
                            />
                        </Box>

                        <Box sx={{ width: { xs: '100%', sm: '50%' } }}>
                            <Controller
                                name="passwordConfirm"
                                control={formMethods.control}
                                rules={{
                                    required:
                                        formMode === 'create' || watchPassword
                                            ? 'La confirmation du mot de passe est requise'
                                            : false,
                                    validate: (value) => {
                                        if ((formMode === 'create' || watchPassword) && value !== watchPassword) {
                                            return 'Les mots de passe ne correspondent pas';
                                        }
                                        return true;
                                    },
                                }}
                                render={({ field, fieldState }) => (
                                    <PasswordInput
                                        {...field}
                                        label={'Confirmer le mot de passe'}
                                        placeholder="Confirmez le mot de passe"
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                        autoComplete="off"
                                        fullWidth
                                    />
                                )}
                            />
                        </Box>

                        {/* Password requirements */}
                        <Stack direction="row" spacing="24px">
                            <Stack spacing="12px">
                                <RequirementItem met={requirements.length} text="celui-ci doit comporter au moins 8" />
                                <RequirementItem met={requirements.cases} text="une majuscule et une minuscule," />
                            </Stack>
                            <Stack spacing="12px">
                                <RequirementItem met={requirements.digit} text="un chiffre" />
                                <RequirementItem met={requirements.special} text="un caractère spécial (.,;:!?+-_@#$)" />
                            </Stack>
                        </Stack>
                    </Stack>

                    {/* Footer buttons */}
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Button
                            size="large"
                            onClick={onCancel}
                            disabled={isSubmitting}
                            variant="text"
                            type="button"
                            color="secondary"
                        >
                            Annuler
                        </Button>
                        <Button
                            size="large"
                            type="submit"
                            variant="contained"
                            disabled={isSubmitting}
                            endIcon={
                                <i
                                    className="fi fi-br-arrow-small-right"
                                    style={{ fontSize: 16, display: 'flex', alignItems: 'center' }}
                                />
                            }
                        >
                            {formMode === 'create' ? 'Créer' : 'Enregistrer'}
                        </Button>
                    </Stack>
                </Box>
            </form>
        </FormProvider>
    );
};

export default UpercutForm;
